// src/hr/kit/DayCells.js -- one cell per day of an allowance.
// Reads left to right: lit = available to book, outlined = scheduled, empty = used. Time is
// counted in half days, so a cell can be split (e.g. half lit, half empty after a half day).
import React from 'react';
import { daysLabel, daysNumber } from '../format';
import { cx } from './util';

const HALF_DAY = 4;
const MAX_PER_ROW = 20;
const MAX_PER_ROW_SMALL = 10;

const WHOLE = { a: 'available', s: 'scheduled', u: 'used' };
const SPLIT = { au: 'half', as: 'available-scheduled', su: 'scheduled-used' };

const halves = (hours) => Math.max(0, Math.round((Number(hours) || 0) / HALF_DAY));

// Even rows: 25 days -> 13 + 12 rather than 20 + 5.
function columnsFor(cells, perRow) {
  const rows = Math.ceil(cells / perRow);
  return Math.ceil(cells / rows);
}

export function cellStates(totalHours, usedHours, scheduledHours) {
  const total = Math.max(0, Number(totalHours) || 0);
  const cells = Math.ceil(total / 8);
  const cap = Math.min(cells * 2, halves(total));
  const used = Math.min(halves(usedHours), cap);
  const scheduled = Math.min(halves(scheduledHours), cap - used);
  const available = cap - used - scheduled;

  const kindAt = (i) => {
    if (i < available) return 'a';
    if (i < available + scheduled) return 's';
    return 'u'; // used, or past the end of a partial last day
  };
  const states = [];
  for (let c = 0; c < cells; c += 1) {
    const left = kindAt(c * 2);
    const right = kindAt(c * 2 + 1);
    states.push(left === right ? WHOLE[left] : SPLIT[left + right]);
  }
  return { states, available, scheduled, used };
}

export function DayCells({ totalHours = 0, usedHours = 0, scheduledHours = 0, legend = true, ariaLabel, className }) {
  const { states, available, scheduled, used } = cellStates(totalHours, usedHours, scheduledHours);
  if (states.length === 0) return null;

  const summary = [`${daysNumber(available * HALF_DAY)} of ${daysLabel(totalHours)} available`];
  if (scheduled) summary.push(`${daysNumber(scheduled * HALF_DAY)} scheduled`);
  if (used) summary.push(`${daysNumber(used * HALF_DAY)} used`);

  const style = {
    '--hrp-cols': columnsFor(states.length, MAX_PER_ROW),
    '--hrp-cols-sm': columnsFor(states.length, MAX_PER_ROW_SMALL)
  };

  return (
    <div className={cx('hrp-daycells', className)}>
      <div className="hrp-cells" role="img" aria-label={ariaLabel || summary.join(', ')} style={style}>
        {states.map((state, i) => (
          <i key={i} className="hrp-cell" data-state={state} style={{ '--hrp-i': i }} />
        ))}
      </div>
      {legend && (
        <ul className="hrp-legend" aria-hidden="true">
          <li><i className="hrp-legend__key hrp-legend__key--available" />Available</li>
          <li><i className="hrp-legend__key hrp-legend__key--scheduled" />Scheduled</li>
          <li><i className="hrp-legend__key hrp-legend__key--used" />Used</li>
        </ul>
      )}
    </div>
  );
}

export default DayCells;
